/**
 * "Tell me when there is room."
 *
 * The service is invite-only, so this is the only thing the front page can
 * honestly ask a stranger for. It appears twice — once in the hero and once at
 * the end — because somebody who reads the whole page should not have to scroll
 * back up to act on it.
 *
 * Two details are deliberate and easy to get wrong:
 *
 *  - **Submit is never disabled before submission.** An empty or malformed
 *    address should produce a message that says so, not a button that silently
 *    does nothing and leaves somebody wondering whether the page is broken.
 *  - **The success message is the same whether or not the address was already
 *    on the list.** Saying "you are already signed up" would let anybody test
 *    whether a given person had signed up, which is not a question a public
 *    form should answer about a private list.
 */

import { useId, useRef, useState } from "react";
import { trpcClient } from "../../lib/trpc.js";
import { useT } from "../../lib/i18n.js";
import "./WaitlistForm.css";

type Phase = "idle" | "sending" | "done" | "error";

export function WaitlistForm({
  /** Distinguishes the hero copy from the closing copy. */
  tone = "hero",
}: {
  tone?: "hero" | "closing";
}) {
  const t = useT();
  const inputId = useId();
  const statusId = useId();
  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (phase === "sending") return;

    const trimmed = email.trim();

    /*
     * Checked here as well as on the server, so somebody who mistypes gets an
     * answer immediately rather than after a round trip. The server is still
     * the one that decides — this is a courtesy, not a gate.
     */
    if (!trimmed) {
      setPhase("error");
      setMessage(t("Add your email address and we will let you know."));
      inputRef.current?.focus();
      return;
    }
    if (
      !trimmed.includes("@") ||
      trimmed.startsWith("@") ||
      trimmed.endsWith("@")
    ) {
      setPhase("error");
      setMessage(t("That does not look like an email address."));
      inputRef.current?.focus();
      return;
    }

    setPhase("sending");
    setMessage(null);

    try {
      await trpcClient.waitlist.join.mutate({
        email: trimmed,
        /* The language they were reading, so the invitation can be written in
           it. Nothing else about the browser is sent. */
        locale: navigator.language || undefined,
      });
      setPhase("done");
      setEmail("");
    } catch {
      setPhase("error");
      setMessage(
        t(
          "That did not go through. Check your connection and try again in a moment.",
        ),
      );
    }
  };

  if (phase === "done") {
    return (
      <p className="fc-waitlist__done" role="status">
        <CheckGlyph />
        {t(
          "You are on the list. We will write when there is room — nothing else, ever.",
        )}
      </p>
    );
  }

  return (
    <form className="fc-waitlist" onSubmit={(e) => void submit(e)} noValidate>
      <label className="fc-waitlist__label" htmlFor={inputId}>
        {t("Email address")}
      </label>

      <div className="fc-waitlist__row">
        <input
          id={inputId}
          ref={inputRef}
          className="fc-waitlist__input"
          type="email"
          name="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            if (phase === "error") {
              setPhase("idle");
              setMessage(null);
            }
          }}
          placeholder="you@example.com"
          autoComplete="email"
          inputMode="email"
          spellCheck={false}
          aria-describedby={message ? statusId : undefined}
          aria-invalid={phase === "error" || undefined}
        />

        <button
          type="submit"
          className="fc-waitlist__submit"
          data-busy={phase === "sending" || undefined}
        >
          {/* The label stays put while it works — a button whose text changes
              to "Sending…" moves the thing you just clicked. */}
          <span>
            {tone === "hero" ? t("Get an invite") : t("Join the list")}
          </span>
          {phase === "sending" && <Spinner />}
        </button>
      </div>

      {/* Polite, so a screen reader finishes the current sentence first. */}
      <p
        className="fc-waitlist__status"
        id={statusId}
        role="status"
        aria-live="polite"
      >
        {message ??
          t(
            "Invite-only while we grow. One email when a place opens, and no other mail.",
          )}
      </p>
    </form>
  );
}

function Spinner() {
  return (
    <svg
      className="fc-waitlist__spinner"
      viewBox="0 0 16 16"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="6" />
    </svg>
  );
}

function CheckGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="fc-waitlist__check">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
